import { useMyStars } from "@/src/hooks/features/rewardly/use-my-stars";
import { useTheme } from "@/src/styles/theme-provider";
import { AddCircle, Send2, SmsStar } from "iconsax-react-nativejs";
import { useTranslation } from "react-i18next";
import { Pressable, ScrollView, Text, TextInput, View } from "react-native";
import InviteMembersModal from "../../action-modals/add-members/add-members-modal";
import CapacityCounter from "../../events/event-form/capacity-counter";
import ScButton from "../../ui-kit/button";
import StarBurstAnimation from "./star-burst-animation";
import UserChip from "./user-chip";

const RecognizeUser = () => {
	const { t } = useTranslation();
	const { themeVars } = useTheme();
	const myStars = useMyStars();
	const primaryColor = themeVars["--color-primary-default"] as string;

	return (
		<View className="flex-col md:flex-1 md:border md:border-ash-3 bg-chalk rounded-xl p-4 md:p-5">
			<View className="flex-1 flex-col gap-4">
				<View className="flex-row items-center gap-2">
					<SmsStar size={28} color={primaryColor} />
					<Text className="text-lg md:text-xl font-semibold text-onyx">
						{t("LABEL_RECOGNIZE_A_COLLEAGUE")}
					</Text>
				</View>

				<View className="flex-col gap-2">
					<Text className="text-sm text-onyx">{t("LABEL_RECIPIENT_FIELD")}</Text>
					{myStars.selectedUsers?.length > 0 ? (
						<ScrollView horizontal showsHorizontalScrollIndicator={false} className="flex-row gap-2">
							{myStars.selectedUsers.map((user, i) => (
								<UserChip key={i} user={user} onRemove={() => myStars.removeUser(user)} />
							))}
							<Pressable
								onPress={() => myStars.setUsersModalVisible(true)}
								className="flex-row items-center gap-1 px-3 py-2 border border-dashed border-ash-3 rounded-xl hover:bg-ash-3"
							>
								<AddCircle size={16} color={primaryColor} />
								<Text className="text-xs text-primary">{t("BTN_ADD")}</Text>
							</Pressable>
						</ScrollView>
					) : (
						<Pressable
							onPress={() => myStars.setUsersModalVisible(true)}
							className="flex-row items-center gap-3 bg-ash rounded-xl px-4 py-3 md:border md:border-ash-3 hover:bg-ash-3"
						>
							<View className="flex-1">
								<Text className="text-sm text-ash-2">{t("LABEL_SELECT_A_COLLEAGUE")}</Text>
							</View>
							<AddCircle size={20} color={primaryColor} />
						</Pressable>
					)}
				</View>

				<View className="flex-col gap-2">
					<Text className="text-sm text-onyx">{t("LABEL_STARS_TO_GIVE")}</Text>
					<CapacityCounter
						value={myStars.star}
						onChange={myStars.setStars}
						min={myStars.currentSeason?.minGiveAmt ?? 1}
						max={myStars.currentSeason?.maxGiveAmt ?? 9999}
					/>
				</View>

				<View className="flex-col gap-2">
					<Text className="text-sm text-onyx">{t("LABEL_REASON")}</Text>
					<TextInput
						placeholder={t("LABEL_RECOGNITION_REASON_PLACEHOLDER")}
						multiline
						value={myStars.reason}
						onChangeText={myStars.setReason}
						className="min-h-[120px] px-4 py-3 text-sm text-onyx focus:outline-none bg-ash md:border md:border-ash-3 rounded-2xl"
						selectionColor={primaryColor}
						placeholderTextColor={themeVars["--color-ash-2"] as string}
						underlineColorAndroid="transparent"
					/>
				</View>
			</View>
			<StarBurstAnimation visible={myStars.showStarBurstAnimation} setShowAnimation={myStars.setShowStarBurstAnimation} />
			<ScButton
				text="BTN_SEND_STARS"
				onPress={myStars.handleSendStars}
				loading={myStars.isSending}
				iconPosition="right"
				icon={<Send2 variant="Bold" />}
				className="mt-4"
			/>

			<InviteMembersModal
				members={myStars.selectedUsers}
				visible={myStars.usersModalVisible}
				onClose={() => myStars.setUsersModalVisible(false)}
				onInvite={(selectedUsers) => {
					myStars.setSelectedUsers(selectedUsers)
					myStars.setUsersModalVisible(false)
				}}
			/>
		</View>
	);
};

export default RecognizeUser;
